import React from 'react'

import type { RouteDef } from '@ztechapps/common.contract'
import type { ShortcutConfig } from '@ztechapps/webui.util'

import { blogPermissions } from '@ztechapps/blog.common'
import { useConfirmDialog, useError, useHistory, useMutation, useRole } from '@ztechapps/common.hook'
import { BusyButton } from '@ztechapps/webui.button'
import { WebuiIocKey, useInject } from '@ztechapps/webui.contract'
import { formatShortcutTitle, useKeyboardShortcut, useKeyboardShortcutContext } from '@ztechapps/webui.util'

import { useBlogItemContext } from './Context'
import { queries } from './graphql'

export type DeleteButtonProps = {
  /** The host's Delete shortcut entry — registered only while the user may delete. */
  deleteShortcut: ShortcutConfig
}

export const DeleteButton = ({ deleteShortcut }: DeleteButtonProps) => {
  const { document } = useBlogItemContext()
  const [remove, , { loading }] = useMutation({ ...queries.deleteConfig })
  const { hasRole } = useRole({ roleName: blogPermissions.canDelete.value })
  const history = useHistory()
  const blogListRoute = useInject<RouteDef>(WebuiIocKey.routes.blogList)
  const { getResolvedKeys } = useKeyboardShortcutContext()

  const { onConfirm } = useConfirmDialog({
    title: 'Are you sure you want to delete this blog entry?',
    text: '',
    confirmed: async () => {
      try {
        await remove({ _id: document?._id })
        return history.push(blogListRoute.path)
      } catch (e) {
        onError(e)
      }
    },
  })
  const { onError } = useError()

  useKeyboardShortcut({ ...deleteShortcut, action: onConfirm, enabled: hasRole })

  return (
    <BusyButton
      disabledReason={hasRole ? undefined : 'You do not have permission to delete this blog entry.'}
      icon="trash"
      text="Delete..."
      title={formatShortcutTitle('Delete this blog entry?', getResolvedKeys(deleteShortcut.id))}
      bsStyle="danger"
      isBusy={loading}
      onClick={onConfirm}
    />
  )
}
